import type { CSSProperties } from 'react';
import React, { useState } from 'react';
import { useMeasure } from 'react-use';
import {
  DRAWER_HEIGHT_CSS_VAR,
  DrawerContainer,
  DrawerContent,
  DrawerHeader,
  IconClose,
  PageSlider,
} from '@noths/polaris-client-ribbons-design-system';
import { ICON_BUTTON_TEXT_CLASS, iconButton } from '@noths/polaris-client-styles';

import { FooterAppPromo } from '../../FooterAppPromo/FooterAppPromo';
import type { NavigationDrilldownProps } from '../types';
import { NavigationDrilldownList } from './NavigationDrilldownList';
import * as styles from 'client-site-chrome-ui/components/molecules/NavigationDrilldown/styles/NavigationDrilldown.styles';
import { LocalePanel } from 'client-site-chrome-ui/components/molecules/LocalePanel/LocalePanel';
import { useNavigation } from 'client-site-chrome-ui/components/molecules/NavigationDrilldown/hooks/useNavigation/useNavigation';
import { useTracking } from 'client-site-chrome-ui/components/molecules/NavigationDrilldown/hooks/useTracking/useTracking';

export const NavigationDrilldownDrawer = ({ navigation, onClose }: NavigationDrilldownProps) => {
  const {
    onBack,
    onSelect,
    state: { currentPage, pages },
  } = useNavigation(navigation);

  const tracking = useTracking();
  const [drawerHeaderRef, drawerHeaderRect] = useMeasure<HTMLDivElement>();
  const [expandedState, setExpandedState] = useState<Record<number, number | null>>({});

  const setExpandedIndex = (index: number | null, level: number = 0) => {
    setExpandedState((prev: Record<number, number | null>) => {
      const newState = {
        ...prev,
        [level]: index,
      };
      return newState;
    });
  };

  const getExpandedIndex = (level: number = 0) => {
    return expandedState[level] || null;
  };

  return (
    <div
      css={styles.container}
      style={
        {
          [DRAWER_HEIGHT_CSS_VAR]: `calc(${window.innerHeight}px - ${drawerHeaderRect.height}px)`,
        } as CSSProperties
      }
    >
      <div css={styles.headerPageSliderWrapper} ref={drawerHeaderRef}>
        <PageSlider pageIndex={currentPage} transition={styles.headerPageSliderTransition}>
          {pages.map((page) => (
            <DrawerHeader
              index={page.index}
              key={page.title}
              onBack={
                page.index !== 0
                  ? () => {
                      onBack();
                      setExpandedState({});
                      tracking.onBack(page.index - 1);
                    }
                  : undefined
              }
              title={page.title}
            />
          ))}
        </PageSlider>
      </div>

      <PageSlider pageIndex={currentPage} transition={styles.contentPageSliderTransition}>
        {pages.map((page) => {
          const disabled = page.index !== currentPage;
          const firstPage = page.index === 0;

          return (
            <DrawerContainer key={page.title}>
              {!page.isEmpty && (
                <>
                  <DrawerContent>
                    <NavigationDrilldownList
                      disabled={disabled}
                      expandedIndex={getExpandedIndex(1)}
                      getExpandedIndex={getExpandedIndex}
                      items={page.items || []}
                      level={0}
                      onSelect={onSelect}
                      setExpandedIndex={setExpandedIndex}
                    />
                    {firstPage && (
                      <>
                        <div css={styles.appPanel}>
                          <FooterAppPromo />
                        </div>
                        <div css={styles.localePanel}>
                          <LocalePanel placement="navigation" />
                        </div>
                      </>
                    )}
                  </DrawerContent>
                </>
              )}
            </DrawerContainer>
          );
        })}
      </PageSlider>
      <button css={[iconButton, styles.closeBtn]} onClick={onClose}>
        <IconClose height={24} width={24} />
        <span className={ICON_BUTTON_TEXT_CLASS}>Close</span>
      </button>
    </div>
  );
};
